<script setup lang="ts">
// Sign in. Foodics people use their Google account (Zoho is off since 2026-10-18); accounts an admin
// created (invited outsiders, the e2e test user) use Cognito's own email and
// password form. Cognito authenticates every path; there is no self sign-up.
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { rememberDestination } from "@/lib/post-signin";
import { useTheme } from "@/lib/theme";
import BrandLockup from "@/components/ui/BrandLockup.vue";
import Icon from "@/components/ui/Icon.vue";
import Constellation from "@/components/ui/Constellation.vue";

const auth = useAuthStore();
const route = useRoute();
const { theme, toggle } = useTheme();
const email = ref("");
const busy = ref<"" | "Google" | "COGNITO">("");

function signIn(idp: "Google" | "COGNITO") {
  busy.value = idp;
  rememberDestination(route.query.redirect); // Cognito returns to the bare origin
  void auth.startSignIn(idp, idp === "COGNITO" && email.value.trim() ? email.value.trim() : undefined);
}
</script>

<template>
  <div class="auth">
    <header class="auth-top">
      <BrandLockup />
      <button class="icon-btn" :aria-label="`Switch to the ${theme === 'dark' ? 'light' : 'dark'} theme`" @click="toggle">
        <Icon :name="theme === 'dark' ? 'sun' : 'moon'" />
      </button>
    </header>
    <main class="auth-card">
      <section class="art" aria-hidden="true">
        <span class="eyebrow brand">Majlis / from intent to impact</span>
        <p class="headline">Great work.<br />A little more<br /><span>autonomous.</span></p>
        <p class="sub">Your code, your context, your decisions. A team of agents that moves with you.</p>
        <Constellation class="graph" />
      </section>
      <section class="form" aria-labelledby="signin-h">
        <span class="eyebrow">Welcome to Majlis</span>
        <h1 id="signin-h">Your next idea starts here.</h1>
        <p class="lead">Sign in to your organisation and put your agents to work.</p>

        <button class="btn btn--primary btn--block big" :disabled="!!busy" @click="signIn('Google')">
          <span v-if="busy === 'Google'" class="spinner" aria-hidden="true" />Continue with Google
        </button>

        <div class="or"><span>or with an account an admin invited</span></div>
        <form class="invited" @submit.prevent="signIn('COGNITO')">
          <label class="control" for="signin-email">Work email
            <input id="signin-email" v-model="email" class="field" type="email" autocomplete="email" placeholder="you@company.com" />
          </label>
          <button class="btn btn--block big" type="submit" :disabled="!!busy">
            <span v-if="busy === 'COGNITO'" class="spinner" aria-hidden="true" />Continue with email and password <Icon name="arrow" />
          </button>
        </form>
        <p class="fine">Foodics accounts sign in with Google. Email and password are for accounts an admin created; there is no self sign-up.</p>
      </section>
    </main>
  </div>
</template>

<style scoped>
.auth { min-height: 100vh; display: flex; flex-direction: column; padding: var(--space-6) var(--space-7) var(--space-7); background: var(--bg); }
.auth-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--space-6); }
.auth-card {
  flex: 1; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); border: 1px solid var(--line); border-radius: var(--radius-l);
  overflow: hidden; background: var(--bg-elev); max-width: 1320px; width: 100%; margin: 0 auto; min-height: 560px;
}
.art { position: relative; display: flex; flex-direction: column; justify-content: center; padding: var(--space-8); background: var(--graph-bg); border-right: 1px solid var(--line); overflow: hidden; }
.headline { font-size: var(--text-2xl); line-height: 1.12; font-weight: 500; letter-spacing: -1.8px; margin: var(--space-5) 0 var(--space-5); }
.headline span { color: var(--brand); }
.sub { color: var(--muted); max-width: 380px; font-size: var(--text-sm); }
.graph { width: 100%; max-width: 560px; margin-top: var(--space-6); }
.form { display: flex; flex-direction: column; justify-content: center; padding: var(--space-8); max-width: 560px; width: 100%; margin: 0 auto; }
.form h1 { font-size: var(--text-xl); font-weight: 600; letter-spacing: -.6px; margin: var(--space-4) 0 var(--space-3); }
.lead { color: var(--muted); font-size: var(--text-sm); margin-bottom: var(--space-6); }
.big { min-height: 42px; font-size: var(--text-sm); margin-bottom: var(--space-3); }
.or { display: flex; align-items: center; gap: var(--space-4); margin: var(--space-5) 0 var(--space-4); font-size: var(--text-xs); color: var(--muted); }
.or::before, .or::after { content: ""; flex: 1; height: 1px; background: var(--line); }
.invited { display: flex; flex-direction: column; gap: var(--space-3); }
.invited .control { margin-bottom: var(--space-1); }
.fine { font-size: var(--text-xs); color: var(--faint); margin-top: var(--space-5); }
@media (max-width: 900px) {
  .auth { padding: var(--space-5); }
  .auth-card { grid-template-columns: 1fr; min-height: 0; }
  .art { padding: var(--space-6) var(--space-6); border-right: 0; border-bottom: 1px solid var(--line); }
  .headline { font-size: var(--text-2xl); }
  .graph { display: none; }
  .form { padding: var(--space-6) var(--space-6); }
}
</style>
